import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
    Users,
    ShieldCheck,
    MailCheck,
    MailX,
    UserCheck,
    UserX,
    Search,
    Loader2,
    RefreshCcw,
    ArrowLeft,
    UserMinus,
    UserPlus,
    Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("it-IT", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

function StatCard({ icon: Icon, label, value }) {
    return (
        <Card className="border-slate-200">
            <CardContent className="pt-5 pb-5">
                <Icon className="size-4 text-primary/70" />
                <div className="mt-2 text-2xl font-serif font-semibold text-slate-900">{value}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
            </CardContent>
        </Card>
    );
}

function RoleBadge({ role }) {
    return role === "admin" ? (
        <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/10">
            <ShieldCheck className="size-3 mr-1" /> Admin
        </Badge>
    ) : (
        <Badge variant="outline" className="border-slate-300 text-slate-700">
            Utente
        </Badge>
    );
}

function StatusBadge({ active }) {
    return active ? (
        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
            Attivo
        </Badge>
    ) : (
        <Badge className="bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-50">
            Sospeso
        </Badge>
    );
}

function VerifiedBadge({ verified }) {
    return verified ? (
        <Badge className="bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-50">
            <MailCheck className="size-3 mr-1" /> Verificata
        </Badge>
    ) : (
        <Badge className="bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-50">
            <MailX className="size-3 mr-1" /> Non verificata
        </Badge>
    );
}

export default function AdminUsers() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [roleF, setRoleF] = useState("all");
    const [statusF, setStatusF] = useState("all");
    const [verifiedF, setVerifiedF] = useState("all");
    const [search, setSearch] = useState("");
    const [debounced, setDebounced] = useState("");

    const [pending, setPending] = useState(null); // {user, action: 'suspend' | 'reactivate'}
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    const params = useMemo(() => {
        const p = {};
        if (roleF !== "all") p.role = roleF;
        if (statusF !== "all") p.status = statusF;
        if (verifiedF !== "all") p.verified = verifiedF;
        if (debounced) p.search = debounced;
        return p;
    }, [roleF, statusF, verifiedF, debounced]);

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await API.get("/users", { params });
            setUsers(data.users || []);
            setCounts(data.counts || {});
        } catch (err) {
            setError(apiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleF, statusF, verifiedF, debounced]);

    const resetFilters = () => {
        setRoleF("all");
        setStatusF("all");
        setVerifiedF("all");
        setSearch("");
    };

    const doAction = async () => {
        if (!pending) return;
        const { user: target, action } = pending;
        setBusyId(target.id);
        try {
            if (action === "delete") {
                await API.delete(`/users/${target.id}`);
                toast.success(`${target.full_name} è stato eliminato`);
            } else {
                const path = action === "suspend" ? "suspend" : "reactivate";
                await API.post(`/users/${target.id}/${path}`);
                toast.success(
                    action === "suspend"
                        ? `${target.full_name} è stato sospeso`
                        : `${target.full_name} è stato riattivato`,
                );
            }
            setPending(null);
            await load();
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setBusyId(null);
        }
    };

    const activeFiltersCount =
        (roleF !== "all" ? 1 : 0) +
        (statusF !== "all" ? 1 : 0) +
        (verifiedF !== "all" ? 1 : 0) +
        (debounced ? 1 : 0);

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
            <div className="mb-6">
                <Button
                    asChild
                    variant="ghost"
                    size="sm"
                    className="mb-2 -ml-2 text-muted-foreground"
                    data-testid="admin-users-back"
                >
                    <Link to="/admin">
                        <ArrowLeft className="size-4 mr-1" /> Panoramica admin
                    </Link>
                </Button>
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                            <ShieldCheck className="size-3.5" /> Pannello amministratore
                        </div>
                        <h1
                            data-testid="admin-users-title"
                            className="mt-1 font-serif text-3xl lg:text-4xl font-semibold"
                        >
                            Utenti registrati
                        </h1>
                        <p className="mt-2 text-muted-foreground text-sm">
                            Registro completo di chi si è iscritto. Sospendi o riattiva un utente in
                            un clic.
                        </p>
                    </div>
                    <Button
                        onClick={load}
                        variant="outline"
                        data-testid="admin-users-refresh"
                        disabled={loading}
                    >
                        {loading ? (
                            <Loader2 className="size-4 mr-2 animate-spin" />
                        ) : (
                            <RefreshCcw className="size-4 mr-2" />
                        )}
                        Aggiorna
                    </Button>
                </div>
            </div>

            <div className="grid gap-3 grid-cols-2 lg:grid-cols-6 mb-6">
                <StatCard icon={Users} label="Totale" value={counts.total ?? 0} />
                <StatCard icon={UserCheck} label="Attivi" value={counts.active ?? 0} />
                <StatCard icon={UserX} label="Sospesi" value={counts.suspended ?? 0} />
                <StatCard icon={MailCheck} label="Verificati" value={counts.verified ?? 0} />
                <StatCard icon={MailX} label="Non verificati" value={counts.unverified ?? 0} />
                <StatCard icon={ShieldCheck} label="Admin" value={counts.admins ?? 0} />
            </div>

            <Card className="border-slate-200 mb-6">
                <CardContent className="pt-5">
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
                        <div className="lg:col-span-2 relative">
                            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                data-testid="admin-users-search"
                                placeholder="Cerca per nome o email…"
                                className="pl-9"
                            />
                        </div>
                        <Select value={roleF} onValueChange={setRoleF}>
                            <SelectTrigger data-testid="admin-users-role-filter">
                                <SelectValue placeholder="Ruolo" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutti i ruoli</SelectItem>
                                <SelectItem value="user">Solo utenti</SelectItem>
                                <SelectItem value="admin">Solo admin</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={statusF} onValueChange={setStatusF}>
                            <SelectTrigger data-testid="admin-users-status-filter">
                                <SelectValue placeholder="Stato" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutti gli stati</SelectItem>
                                <SelectItem value="active">Solo attivi</SelectItem>
                                <SelectItem value="suspended">Solo sospesi</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={verifiedF} onValueChange={setVerifiedF}>
                            <SelectTrigger data-testid="admin-users-verified-filter">
                                <SelectValue placeholder="Verifica" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Verifica email · tutti</SelectItem>
                                <SelectItem value="yes">Solo verificati</SelectItem>
                                <SelectItem value="no">Solo non verificati</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {activeFiltersCount > 0 && (
                        <div className="mt-4 flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">
                                {activeFiltersCount} filtr{activeFiltersCount === 1 ? "o" : "i"}{" "}
                                attiv{activeFiltersCount === 1 ? "o" : "i"} ·{" "}
                                <strong className="text-slate-800">{users.length}</strong>{" "}
                                risultati
                            </span>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={resetFilters}
                                data-testid="admin-users-reset-filters"
                            >
                                Azzera filtri
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            {error && (
                <Alert variant="destructive" className="mb-6" data-testid="admin-users-error">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <Card className="border-slate-200">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Utente</TableHead>
                                    <TableHead>Città</TableHead>
                                    <TableHead>Ruolo</TableHead>
                                    <TableHead>Stato</TableHead>
                                    <TableHead>Verifica email</TableHead>
                                    <TableHead>Registrato</TableHead>
                                    <TableHead className="text-right">Azioni</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody data-testid="admin-users-tbody">
                                {loading && users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-14">
                                            <Loader2 className="size-5 mx-auto animate-spin text-muted-foreground" />
                                        </TableCell>
                                    </TableRow>
                                ) : users.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={7}
                                            data-testid="admin-users-empty"
                                            className="text-center py-14 text-muted-foreground text-sm"
                                        >
                                            Nessun utente per i filtri selezionati.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.map((u) => {
                                        const isSelf = me && u.id === me.id;
                                        const isAdmin = u.role === "admin";
                                        const locked = isSelf || isAdmin;
                                        return (
                                            <TableRow
                                                key={u.id}
                                                data-testid={`admin-user-row-${u.id}`}
                                            >
                                                <TableCell>
                                                    <div className="font-medium text-slate-900">
                                                        {u.full_name}
                                                        {isSelf && (
                                                            <span className="ml-2 text-xs text-muted-foreground">
                                                                (tu)
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {u.email}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-sm text-slate-700">
                                                    {u.city || u.province ? (
                                                        <div data-testid={`admin-user-city-${u.id}`}>
                                                            <div className="font-medium text-slate-900">{u.city || "—"}</div>
                                                            {u.province && (
                                                                <div className="text-xs text-muted-foreground uppercase">{u.province}</div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-muted-foreground italic">—</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <RoleBadge role={u.role} />
                                                </TableCell>
                                                <TableCell>
                                                    <StatusBadge active={u.is_active} />
                                                </TableCell>
                                                <TableCell>
                                                    <VerifiedBadge verified={u.email_verified} />
                                                </TableCell>
                                                <TableCell className="text-sm text-slate-600">
                                                    {formatDate(u.created_at)}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {locked ? (
                                                        <span className="text-xs text-muted-foreground italic">
                                                            {isAdmin ? "Protetto" : "—"}
                                                        </span>
                                                    ) : (
                                                        <div className="flex items-center justify-end gap-2">
                                                            {u.is_active ? (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800"
                                                                    onClick={() =>
                                                                        setPending({
                                                                            user: u,
                                                                            action: "suspend",
                                                                        })
                                                                    }
                                                                    disabled={busyId === u.id}
                                                                    data-testid={`admin-user-suspend-${u.id}`}
                                                                >
                                                                    {busyId === u.id ? (
                                                                        <Loader2 className="size-3.5 mr-1.5 animate-spin" />
                                                                    ) : (
                                                                        <UserMinus className="size-3.5 mr-1.5" />
                                                                    )}
                                                                    Sospendi
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800"
                                                                    onClick={() =>
                                                                        setPending({
                                                                            user: u,
                                                                            action: "reactivate",
                                                                        })
                                                                    }
                                                                    disabled={busyId === u.id}
                                                                    data-testid={`admin-user-reactivate-${u.id}`}
                                                                >
                                                                    {busyId === u.id ? (
                                                                        <Loader2 className="size-3.5 mr-1.5 animate-spin" />
                                                                    ) : (
                                                                        <UserPlus className="size-3.5 mr-1.5" />
                                                                    )}
                                                                    Riattiva
                                                                </Button>
                                                            )}
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="text-rose-600 hover:text-rose-800 hover:bg-rose-50 size-8 p-0"
                                                                onClick={() =>
                                                                    setPending({
                                                                        user: u,
                                                                        action: "delete",
                                                                    })
                                                                }
                                                                disabled={busyId === u.id}
                                                                data-testid={`admin-user-delete-${u.id}`}
                                                                aria-label="Elimina utente"
                                                            >
                                                                <Trash2 className="size-4" />
                                                            </Button>
                                                        </div>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            <AlertDialog
                open={!!pending}
                onOpenChange={(open) => !open && setPending(null)}
            >
                <AlertDialogContent data-testid="admin-users-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {pending?.action === "suspend"
                                ? `Sospendere ${pending?.user.full_name}?`
                                : pending?.action === "reactivate"
                                    ? `Riattivare ${pending?.user.full_name}?`
                                    : `Eliminare ${pending?.user.full_name}?`}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {pending?.action === "suspend"
                                ? "L'utente non potrà più accedere all'app né inviare domande. Potrai riattivarlo in qualunque momento."
                                : pending?.action === "reactivate"
                                    ? "L'utente tornerà a poter accedere e utilizzare tutte le funzionalità."
                                    : `L'account (${pending?.user.email}) verrà rimosso in modo permanente. Questa azione è irreversibile.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="admin-users-confirm-cancel">
                            Annulla
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={doAction}
                            data-testid="admin-users-confirm-action"
                            className={
                                pending?.action === "reactivate"
                                    ? "bg-emerald-600 hover:bg-emerald-700"
                                    : "bg-rose-600 hover:bg-rose-700"
                            }
                        >
                            {pending?.action === "suspend"
                                ? "Sospendi utente"
                                : pending?.action === "reactivate"
                                    ? "Riattiva utente"
                                    : "Elimina definitivamente"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
